import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../services/apiUtils';

/**
 * Custom hook for sub-user authentication verification
 * Matches mobile app SplashScreen.js logic for sub-user verification
 * Should be used in builder components to ensure sub-user access is still valid
 */
const useSubUserAuth = (options = {}) => {
  const {
    redirectOnRevoke = '/BuilderLogin',
    showAlert = true,
    enableAutoCheck = true
  } = options;

  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [authStatus, setAuthStatus] = useState('pending'); // 'pending', 'valid', 'invalid'

  const verifySubUserAccess = async () => {
    try {
      setIsChecking(true);

      // Check if user is authenticated first
      const authToken = localStorage.getItem('authToken');
      const userRole = localStorage.getItem('userRole');

      if (!authToken || !userRole) {
        setAuthStatus('invalid');
        navigate(redirectOnRevoke);
        return false;
      }

      // Check if this is a sub-user (matching mobile app logic)
      const isSubUser = localStorage.getItem('isSubUser');
      if (isSubUser === 'true') {
        console.log('🔍 Sub-user detected, verifying access...');

        try {
          // Call the same API endpoint as mobile app
          const response = await apiGet('/api/auth/verify-sub-user');

          if (response.data && !response.data.valid) {
            console.log('❌ Sub-user access revoked:', response.data.message);

            // Clear all auth data (matching mobile app)
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('userRole');
            localStorage.removeItem('@rebax_subscription');
            localStorage.removeItem('isSubUser');
            localStorage.removeItem('actualRole');
            localStorage.removeItem('actualUserId');
            localStorage.removeItem('actualUserCity');
            localStorage.removeItem('parentUserId');
            localStorage.removeItem('actualUserName');

            if (showAlert) {
              alert(response.data.message || 'Your account access has been revoked. Please contact your administrator.');
            }

            setAuthStatus('invalid');
            navigate(redirectOnRevoke);
            return false;
          }

          console.log('✅ Sub-user access verified');
          setAuthStatus('valid');
          return true;
        } catch (error) {
          // Network error - allow through, don't block user (matching mobile app)
          console.log('⚠️ Sub-user verify failed, allowing through:', error.message);
          setAuthStatus('valid');
          return true;
        }
      } else {
        // Not a sub-user, regular user - no verification needed
        console.log('👤 Regular user, no sub-user verification needed');
        setAuthStatus('valid');
        return true;
      }
    } catch (error) {
      console.error('❌ Auth verification error:', error);
      setAuthStatus('valid'); // Allow through on error to avoid blocking legitimate users
      return true;
    } finally {
      setIsChecking(false);
    }
  };

  // Auto-verify on mount if enabled
  useEffect(() => {
    if (enableAutoCheck) {
      verifySubUserAccess();
    }
  }, [enableAutoCheck]);

  // Get sub-user context information
  const getSubUserContext = () => {
    const isSubUser = localStorage.getItem('isSubUser') === 'true';

    if (!isSubUser) {
      return {
        isSubUser: false,
        actualRole: null,
        actualUserId: null,
        actualUserCity: null,
        actualUserName: null,
        parentUserId: null
      };
    }

    return {
      isSubUser: true,
      actualRole: localStorage.getItem('actualRole'),
      actualUserId: localStorage.getItem('actualUserId'),
      actualUserCity: localStorage.getItem('actualUserCity'),
      actualUserName: localStorage.getItem('actualUserName'),
      parentUserId: localStorage.getItem('parentUserId')
    };
  };

  // Get effective role (for routing and permissions)
  const getEffectiveRole = () => {
    return localStorage.getItem('userRole'); // This is already the effective role from login
  };

  return {
    isChecking,
    authStatus,
    verifySubUserAccess,
    getSubUserContext,
    getEffectiveRole
  };
};

export default useSubUserAuth;